// The frame the snippet draws the survey popup in: the iframe pinned next to
// the launcher on a host page. The snippet applies these values to the
// iframe, and the dashboard's survey preview to its stand-in frame, so the
// preview card wraps its text at the same width and stops growing at the
// same height as the real one.

/**
 * The popup's width. Matches the drawer's resting width, so the card's
 * height – measured while the iframe is hidden at that width – stays valid
 * once it shows as the popup.
 */
export const SURVEY_POPUP_WIDTH_PX = 380;

/**
 * The card's own corner and lift, applied to the iframe because the iframe
 * is the only surface the host page can round – the widget draws edge to
 * edge inside it. Violet-tinted so the popup reads as part of the launcher.
 */
export const SURVEY_POPUP_RADIUS = "22px";
export const SURVEY_POPUP_SHADOW =
	"0 18px 44px -12px hsl(260deg 100% 45% / 18%), " +
	"0 2px 8px hsl(260deg 100% 45% / 6%)";

/** The popup never grows past this share of the viewport height. */
const SURVEY_POPUP_MAX_VIEWPORT_SHARE = 0.6;
const SURVEY_POPUP_MIN_HEIGHT_PX = 120;

/**
 * The popup's height for a card of `contentHeight`: the card's own height,
 * at least 120px, at most 60% of the viewport – past that the card scrolls
 * inside the frame.
 */
export const clampSurveyPopupHeight = (
	contentHeight: number,
	viewportHeight: number,
): number =>
	Math.max(
		SURVEY_POPUP_MIN_HEIGHT_PX,
		Math.min(
			Math.ceil(contentHeight),
			viewportHeight * SURVEY_POPUP_MAX_VIEWPORT_SHARE,
		),
	);

/**
 * Theatre mode: the popup grown to a centered panel so image options can be
 * compared side by side. Wide enough for two 4:3 tiles at a readable size,
 * still short of a takeover – the host page stays visible behind the
 * backdrop. CSS lengths, so the viewport units resolve against the host.
 */
export const SURVEY_POPUP_EXPANDED_WIDTH = "min(760px, calc(100vw - 48px))";
export const SURVEY_POPUP_EXPANDED_HEIGHT = "min(88vh, 900px)";

/** The scrim behind the expanded popup. */
export const SURVEY_POPUP_BACKDROP = "hsl(240deg 20% 10% / 25%)";
